import { useEffect, useMemo, useState } from "react"
import { AlertCircle, Check, Clock, Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { VocabularyEntryCreatedByRoleEnum, VocabularyEntryStateEnum } from "@/__generated__/graphql"
import {
  canKeepSpelling,
  correctedFromText,
  entryDisplayText,
  entryStateKey,
  failureCodeKey,
  roleCanKeepSpelling,
} from "@/lib/vocabulary-entries"
import type { CaptureRow } from "@/lib/live/captures-log"
import { cn } from "@/lib/utils"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

type ViewerRole = "student" | "tutor" | "admin" | undefined

export interface VocabularyStripProps {
  rows: CaptureRow[]
  viewerRole: ViewerRole
  onUndo: (entryId: string) => void
  onRetry: (entryId: string) => void
  onKeepSpelling: (entryId: string) => void
  // Entry ids whose revert THIS client asked for and whose row is still
  // corrected — the round trip AND the rebuild that follows it, right up to the
  // broadcast that clears correctedText. For that whole stretch this strip
  // keeps the action mounted and renamed, so the click never loses its focus;
  // the accepted answer's skipCorrection takes it away on the PEER's strip,
  // which is the half that matters, because that is the one that must not offer
  // a second tap. The status region announces the same stretch — not because
  // the button is gone, but because a button renaming itself is not something
  // every screen reader announces.
  restoringIds?: string[]
}

// Mirrors the server's RemoveVocabularyHighlight#can_remove?: the owner (the
// student — every card lands in her deck) may undo at any time and the server
// destroys the materialized cards; the tutor only while the capture is still
// queued inside its 60-second buffer (scheduledAt is the server's job time).
// Shown exactly where the server would say yes, so a strip Undo can never do
// more than the server allows. `now` is injectable for tests.
export function canUndoCapture(row: CaptureRow, viewerRole: ViewerRole, now: number = Date.now()): boolean {
  if (row.state === VocabularyEntryStateEnum.Cancelled) return false
  if (viewerRole === "student") return true
  if (viewerRole === "tutor") return row.state === VocabularyEntryStateEnum.Pending && Date.parse(row.scheduledAt) > now
  return false
}

// "You" when the viewer is the one who clicked capture, else the other role.
function capturedByKey(row: CaptureRow, viewerRole: ViewerRole): string {
  const viewerCaptured =
    (viewerRole === "student" && row.createdByRole === VocabularyEntryCreatedByRoleEnum.Student) ||
    (viewerRole === "tutor" && row.createdByRole === VocabularyEntryCreatedByRoleEnum.Tutor)
  if (viewerCaptured) return "liveSession.vocabularyStrip.capturedBy.you"
  return row.createdByRole === VocabularyEntryCreatedByRoleEnum.Tutor
    ? "liveSession.vocabularyStrip.capturedBy.tutor"
    : "liveSession.vocabularyStrip.capturedBy.student"
}

// Exported so other surfaces showing the same states (e.g. the tutor
// calendar's post-lesson vocabulary list) render the identical icon. The icon
// carries the state's colour; the chip's TEXT never does (see chipClass).
export function StateIcon({ state }: { state: VocabularyEntryStateEnum }) {
  switch (state) {
    case VocabularyEntryStateEnum.Processing:
      return <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
    case VocabularyEntryStateEnum.Completed:
      return <Check className="h-3 w-3 text-success" aria-hidden="true" />
    case VocabularyEntryStateEnum.Failed:
      return <AlertCircle className="h-3 w-3 text-destructive" aria-hidden="true" />
    default:
      return <Clock className="h-3 w-3" aria-hidden="true" />
  }
}

// Chip text stays in the ground's own text colour: --destructive and
// --success measure ~3.5:1 and ~2.6:1 as small text on the light theme, and
// --muted-foreground 4.4:1 on the dark one. The tint and the icon carry the
// state; the label stays readable. Shared with the tutor's post-lesson list.
export function chipClass(state: VocabularyEntryStateEnum): string {
  switch (state) {
    case VocabularyEntryStateEnum.Completed:
      return "bg-success/10 text-foreground"
    case VocabularyEntryStateEnum.Failed:
      return "bg-destructive/10 text-foreground"
    default:
      return "bg-muted text-foreground"
  }
}

// Re-renders every second while a tutor is looking at a queued row whose 60 s
// buffer is still open, so their Undo leaves the screen the moment the server
// would refuse it — instead of staying clickable and answering with a refusal
// the strip never predicted. Idle otherwise.
function useUndoClock(rows: CaptureRow[], viewerRole: ViewerRole): number {
  const [now, setNow] = useState(() => Date.now())
  // Judged against the real clock, not the last tick: after minutes idle a
  // row whose buffer already closed must not show Undo until the first tick.
  const fresh = Date.now()
  const armed =
    viewerRole === "tutor" &&
    rows.some((row) => row.state === VocabularyEntryStateEnum.Pending && Date.parse(row.scheduledAt) > fresh)
  useEffect(() => {
    if (!armed) return
    setNow(Date.now())
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [armed])
  return armed ? now : fresh
}

// The shared "Vocabulary this lesson" strip (#825 phase 2): one chip per
// capture in the lesson — text, who captured it, state (queued → creating →
// created / failed), Undo where the server allows it, Retry on a failure. Rows
// come from the shared captures log, so the peer sees a capture the instant it
// happens; states are refreshed from the subscription by whichever client
// receives it. Mounted by TextEditor between the toolbar and the notes.
export function VocabularyStrip({ rows, viewerRole, onUndo, onRetry, onKeepSpelling, restoringIds = [] }: VocabularyStripProps) {
  const { t } = useTranslation()
  const emptyKey = viewerRole === "tutor" ? "liveSession.vocabularyStrip.emptyTutor" : "liveSession.vocabularyStrip.emptyStudent"
  const now = useUndoClock(rows, viewerRole)
  // Undo on a CREATED capture destroys the materialized cards and their
  // review history on the server (the owner's power, kept); it asks first.
  const [confirmUndo, setConfirmUndo] = useState<CaptureRow | null>(null)
  const restoring = useMemo(() => new Set(restoringIds), [restoringIds])
  const counts = {
    created: rows.filter((r) => r.state === VocabularyEntryStateEnum.Completed).length,
    creating: rows.filter((r) => r.state === VocabularyEntryStateEnum.Pending || r.state === VocabularyEntryStateEnum.Processing).length,
    failed: rows.filter((r) => r.state === VocabularyEntryStateEnum.Failed).length,
  }
  // The in-flight line, role-aware like the label it replaces on the button.
  const restoringKey =
    viewerRole === "tutor"
      ? "liveSession.vocabularyStrip.restoringToastTutor"
      : "liveSession.vocabularyStrip.restoringToast"
  // The revert had no announcement at all: the live region below reported
  // created / creating / failed and said nothing about corrections, and the
  // design deliberately dropped the duplicate toast in favour of the pressed
  // button renaming itself — which some screen readers do not announce
  // reliably. The same sentence rides the region the strip already owns, so it
  // is heard once and no toast reads it a second time. Judged against the rows
  // ON SCREEN, so an id for a row this strip is not showing announces nothing.
  const anyRestoring = rows.some((row) => restoring.has(row.entryId))
  const statusLine = [t("liveSession.vocabularyStrip.statusSummary", counts), anyRestoring ? t(restoringKey) : null]
    .filter((part): part is string => part !== null)
    .join(" · ")

  return (
    <section
      data-testid="vocabulary-strip"
      aria-label={t("liveSession.vocabularyStrip.title")}
      className="shrink-0 border-b border-border bg-card px-3 py-2"
    >
      <div className="mb-1 flex items-center gap-2 text-xs">
        <span className="font-medium text-foreground">{t("liveSession.vocabularyStrip.title")}</span>
        <span className="text-muted-foreground">{t("liveSession.vocabularyStrip.count", { count: rows.length })}</span>
      </div>
      {/* A chip that turns to Failed (and its Retry) would otherwise appear in
          silence; one live summary line announces every change of state — a
          revert going out included (see statusLine). */}
      <p role="status" aria-live="polite" className="sr-only">
        {statusLine}
      </p>

      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t(emptyKey)}</p>
      ) : (
        <ul className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
          {rows.map((row) => {
            const failed = row.state === VocabularyEntryStateEnum.Failed
            // canKeepSpelling is the shared definition of "there is a correction
            // nobody has claimed yet"; roleCanKeepSpelling is the shared bound on
            // WHO may take it — the student always, a tutor only while the
            // capture has not been generated (the server answers a tutor
            // not_found once it has, because the rebuild destroys her cards).
            // Never on a failed row: the correction is written before the media
            // legs, so a media failure leaves the row carrying one with no card
            // to rebuild — the server answers that with not_correctable, and
            // Retry is what such a row actually wants. A surface condition, so
            // it lives here beside the role check and not in the library.
            const correctedFrom = correctedFromText(row.text, row.correctedText)
            const isRestoring = restoring.has(row.entryId)
            // `|| isRestoring` keeps the element on screen through THIS client's
            // own restore. The accepted answer carries the claim, so without it
            // the button would unmount one round trip after the click and drop
            // focus to <body> with nothing having prompted it — asynchronous
            // focus loss, which is worse for a keyboard user than the immediate
            // kind this branch twice ruled out. restoringIds is client-local, so
            // the peer's strip still loses the action on the claim, which is the
            // half that matters: it is the one that must not offer a second tap.
            const keepSpelling =
              !failed && (canKeepSpelling(row) || isRestoring) && roleCanKeepSpelling(viewerRole, row.state)
            // The one label the button shows and the one its accessible name is
            // BUILT from. WCAG 2.5.3 wants the visible label inside the
            // accessible name, and a key that inserted the word mid-sentence
            // satisfied that in English and broke it in every language whose
            // label ends in its verb (German, Dutch, Japanese, Korean) — a
            // voice-control user could not say what they saw. Appending the word
            // to the rendered label holds for every language by construction,
            // rather than depending on each translator noticing.
            const keepSpellingLabel = t(
              viewerRole === "tutor"
                ? "liveSession.vocabularyStrip.keepSpellingTutor"
                : "liveSession.vocabularyStrip.keepSpelling",
            )
            // The word the row is headed by, and the word every action on it
            // names — except the revert, which names what it puts back.
            const displayText = entryDisplayText(row.text, row.correctedText)
            return (
              <li
                key={row.entryId}
                data-testid="vocabulary-strip-row"
                data-entry-id={row.entryId}
                className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-border bg-background px-2 py-1 text-xs"
              >
                {/* The card was made with the corrected spelling, so that is
                    the word this row is about; what was captured is said
                    beside it, never swapped in silently. */}
                <span className="font-medium text-foreground">{displayText}</span>
                {correctedFrom && (
                  <span data-testid="vocabulary-strip-corrected-from" className="text-muted-foreground">
                    {t("liveSession.vocabularyStrip.correctedFrom", { original: correctedFrom })}
                  </span>
                )}
                <span className="text-muted-foreground">{t(capturedByKey(row, viewerRole))}</span>
                <span
                  className={cn("inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5", chipClass(row.state))}
                  title={failed ? t(failureCodeKey(row.failureCode)) : undefined}
                >
                  <StateIcon state={row.state} />
                  {t(entryStateKey(row.outcome))}
                </span>
                {/* The reason is not a tooltip-only fact: readers get it too. */}
                {failed && <span className="sr-only">{t(failureCodeKey(row.failureCode))}</span>}
                {canUndoCapture(row, viewerRole, now) && (
                  <button
                    type="button"
                    className="focus-ring rounded-sm px-1 py-1 text-muted-foreground hover:text-foreground"
                    aria-label={t("liveSession.vocabularyStrip.undoWord", { word: displayText })}
                    onClick={() => (row.state === VocabularyEntryStateEnum.Completed ? setConfirmUndo(row) : onUndo(row.entryId))}
                  >
                    {t("liveSession.vocabularyStrip.undo")}
                  </button>
                )}
                {failed && (
                  <button
                    type="button"
                    className="focus-ring rounded-sm px-1 py-1 font-medium text-foreground hover:underline"
                    aria-label={t("liveSession.vocabularyStrip.retryWord", { word: displayText })}
                    onClick={() => onRetry(row.entryId)}
                  >
                    {t("liveSession.vocabularyStrip.retry")}
                  </button>
                )}
                {keepSpelling && (
                  <button
                    type="button"
                    data-testid="vocabulary-strip-keep-spelling"
                    // The SAME element for the whole restore — the round trip
                    // and the rebuild after it — disabled in place rather than
                    // swapped for a status or dropped when the claim lands: the
                    // click leaves focus here, and unmounting it would drop that
                    // focus to <body> and reflow the row on every single use.
                    // Renaming it is what tells a sighted user; the status
                    // region is what tells a screen reader, which is why the
                    // hook does not also toast the sentence a third time.
                    aria-disabled={isRestoring || undefined}
                    // Named by the ORIGINAL, alone among the row's actions:
                    // that spelling is what the tap puts back, and a strip of
                    // corrected rows would otherwise read as one repeated
                    // label. correctedFrom is non-null wherever this renders.
                    aria-label={
                      isRestoring
                        ? t(restoringKey)
                        : t("liveSession.vocabularyStrip.keepSpellingWord", {
                            label: keepSpellingLabel,
                            word: correctedFrom,
                          })
                    }
                    className={cn(
                      "focus-ring rounded-sm px-1 py-1 text-muted-foreground",
                      isRestoring ? "cursor-default" : "hover:text-foreground",
                    )}
                    onClick={() => {
                      if (!isRestoring) onKeepSpelling(row.entryId)
                    }}
                  >
                    {isRestoring ? t(restoringKey) : keepSpellingLabel}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
      <AlertDialog open={!!confirmUndo} onOpenChange={(open) => !open && setConfirmUndo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("liveSession.vocabularyStrip.undoCreated.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("liveSession.vocabularyStrip.undoCreated.body", {
                word: confirmUndo ? entryDisplayText(confirmUndo.text, confirmUndo.correctedText) : "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("liveSession.vocabularyStrip.undoCreated.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmUndo) onUndo(confirmUndo.entryId)
                setConfirmUndo(null)
              }}
            >
              {t("liveSession.vocabularyStrip.undoCreated.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
